import React, { useCallback, useEffect, useState } from "react";
import { CaretRightOutlined } from "@ant-design/icons";
import { Col, Collapse, Form, message, Pagination, Row, Space } from "antd";
import axios from "axios";

import PageHeader from "../Layout/PageHeader";
import { usePermissions } from "../App/PermissionContext";
import BonusCampaignList from "./BonusCampaignList";
import BonusCampaignForm from "./BonusCampaignForm";
import { PERMISSIONS } from "../../constants/roles";
import BonusCampaignFilters from "./BonusCampaignFilters";

const BonusCampaign = () => {
  const { permissions } = usePermissions();
  const [form] = Form.useForm();

  const [loading, setLoading] = useState({ create: false, index: false });
  const [campaigns, setCampaigns] = useState([]);
  const [pagination, setPagination] = useState({ currentPage: 1, limit: 20 });
  const [filters, setFilters] = useState({});

  const fetchCampaigns = useCallback(async () => {
    setLoading((prev) => ({ ...prev, index: true }));

    try {
      const params = {
        limit: pagination.limit,
        page: pagination.currentPage,
        ...(filters.search ? { search: filters.search } : {}),
        ...(filters.searchField ? { searchField: filters.searchField } : {}),
        ...(filters.language ? { language: filters.language } : {}),
        ...(filters.isActive !== undefined &&
        filters.isActive !== null &&
        filters.isActive !== ""
          ? { isActive: filters.isActive }
          : {}),
        ...(filters.dateRange?.[0]
          ? { startDate: filters.dateRange[0].startOf("day").toISOString() }
          : {}),
        ...(filters.dateRange?.[1]
          ? { endDate: filters.dateRange[1].endOf("day").toISOString() }
          : {}),
        ...(filters.category && { category: filters.category }),
      };

      const res = await axios.get("/api/campaigns", { params });

      setCampaigns(res.data.data || []);
      setPagination((prev) => ({ ...prev, ...res.data.pagination }));
    } catch (error) {
      message.error(error.response?.data?.message || error.message);
    } finally {
      setLoading((prev) => ({ ...prev, index: false }));
    }
  }, [pagination.currentPage, pagination.limit, filters]);

  useEffect(() => {
    fetchCampaigns();
  }, [fetchCampaigns]);

  const handleFiltersChange = (newFilters) => {
    setPagination((prev) => ({
      ...prev,
      currentPage: 1,
    }));
    setFilters(newFilters);
  };

  const normalizeCampaignDetails = (details = []) =>
    (details || [])
      .filter((item) => Number(item?.bonus) > 0)
      .map((item) => ({
        packageAmount: item.packageAmount,
        bonus: item.bonus,
        ...(item.limit !== undefined && item.limit !== null && item.limit !== ""
          ? { limit: item.limit }
          : {}),
      }));

  const createHandler = async (values) => {
    setLoading((prev) => ({ ...prev, create: true }));
    try {
      const formData = new FormData();

      const payload = {
        name: values.name,
        description: values.description || "",
        category: values.category,
        isFirstTopupOnly: values.isFirstTopupOnly,
        startDate: values.campaignPeriod?.[0]
          ? values.campaignPeriod[0].startOf("day").toISOString()
          : "",
        endDate: values.campaignPeriod?.[1]
          ? values.campaignPeriod[1].endOf("day").toISOString()
          : "",
        campaignDetails: normalizeCampaignDetails(values.campaignDetails),
        badge: JSON.stringify({
          en: values.badge_en || "",
          zh: values.badge_zh || "",
          ms: values.badge_ms || "",
        }),
        status: values.status || "",
      };

      await axios.post("/api/campaigns", payload);

      form.resetFields();
      message.success(
        "Campaign created. It is inactive by default — activate it from the list below.",
      );
      fetchCampaigns();
    } catch (error) {
      message.error(error.response?.data?.message || error.message);
    } finally {
      setLoading((prev) => ({ ...prev, create: false }));
    }
  };

  const toggleHandler = async (id, isActive) => {
    try {
      await axios.patch(`/api/campaigns/${id}/toggle`, { isActive });
      message.success(`Campaign ${isActive ? "activated" : "deactivated"}.`);
      fetchCampaigns();
    } catch (error) {
      message.error(error.response?.data?.message || error.message);
    }
  };

  const updateHandler = async (id, data) => {
    try {
      const payload = {
        name: data.name,
        description: data.description || "",
        campaignDetails: normalizeCampaignDetails(data.campaignDetails),
        badge: {
          en: data.badge_en || "",
          zh: data.badge_zh || "",
          ms: data.badge_ms || "",
        },
        isFirstTopupOnly: data.isFirstTopupOnly,
      };

      await axios.put(`/api/campaigns/${id}`, payload);

      message.success("Campaign updated.");
      fetchCampaigns();
    } catch (error) {
      message.error(error.response?.data?.message || error.message);
    }
  };

  const canCreate = permissions?.includes(PERMISSIONS.CREATE_CAMPAIGN);
  const canUpdate = permissions?.includes(PERMISSIONS.UPDATE_CAMPAIGN);

  return (
    <div>
      <PageHeader
        title="Bonus Campaign"
        childRoutes={[
          { path: "/bonus-campaign", breadcrumbName: "Bonus Campaign" },
        ]}
      />

      <Space direction="vertical" size="middle" style={{ display: "flex" }}>
        {canCreate && (
          <Collapse
            expandIcon={({ isActive }) => (
              <CaretRightOutlined rotate={isActive ? 90 : 0} />
            )}
          >
            <Collapse.Panel header="Create New Campaign">
              <BonusCampaignForm
                form={form}
                loading={loading.create}
                mode="create"
                onSubmit={createHandler}
                onReset={() => form.resetFields()}
                submitText="Create Campaign"
              />
            </Collapse.Panel>
          </Collapse>
        )}

        <Collapse
          expandIcon={({ isActive }) => (
            <CaretRightOutlined rotate={isActive ? 90 : 0} />
          )}
          style={{ marginTop: 16 }}
        >
          <Collapse.Panel header="Filters" key="filters">
            <BonusCampaignFilters
              filters={filters}
              onFiltersChange={handleFiltersChange}
            />
          </Collapse.Panel>
        </Collapse>

        <BonusCampaignList
          campaigns={campaigns}
          loading={loading.index}
          canUpdate={canUpdate}
          onToggle={toggleHandler}
          onUpdate={updateHandler}
        />

        <Row justify="end">
          <Col>
            <Pagination
              total={pagination?.totalSize}
              pageSize={pagination?.limit}
              current={pagination?.currentPage}
              onChange={(currentPage, limit) =>
                setPagination((prev) => ({ ...prev, currentPage, limit }))
              }
            />
          </Col>
        </Row>
      </Space>
    </div>
  );
};

export default BonusCampaign;
